import React, { useState, useEffect } from 'react';
import './App.css';
import SudokuBoard from './components/SudokuBoard';
import GameControls from './components/GameControls';
import GameOver from './components/GameOver';
import Tutorial from './components/Tutorial';
import MainMenu from './components/MainMenu';
import SudokuBombGame from './components/SudokuBombGame';
import BombGameResult from './components/BombGameResult';
import BombGameTutorial from './components/BombGameTutorial';
import NormalSudokuGame from './components/NormalSudokuGame';
import SudokuResult from './components/SudokuResult';
import { 
  generateCompletedSudoku, 
  hasUniqueSolution, 
  findOptimalSolution,
  findLastRemovableCellBoard,
  findAllRemovableCells,
  explainWhyNotRemovable,
  checkSudokuValidity
} from './utils/sudokuUtils';

function App() {
  const [showMainMenu, setShowMainMenu] = useState(true);
  const [gameMode, setGameMode] = useState('normal'); // 'normal', 'bomb', 'sudoku'
  const [board, setBoard] = useState(null);
  const [originalBoard, setOriginalBoard] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [removedCells, setRemovedCells] = useState(0);
  const [lastRemoved, setLastRemoved] = useState(null);
  const [multipleAnswers, setMultipleAnswers] = useState([]);
  const [optimalSolution, setOptimalSolution] = useState(null);
  const [showOptimalSolution, setShowOptimalSolution] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialRemovalInProgress, setInitialRemovalInProgress] = useState(false);
  
  // 数独危機一髪モード用の状態
  const [removableCells, setRemovableCells] = useState([]);
  const [bombGameResult, setBombGameResult] = useState(null);
  
  // おまけ：数独モード用の状態
  const [sudokuCheckResult, setSudokuCheckResult] = useState(null);

  // ゲーム開始時に指定した数のマスを自動的に消去する関数
  const removeInitialCells = (fullBoard, count) => {
    // 盤面のコピーを作成
    const boardCopy = fullBoard.map(row => [...row]);
    const cellsToRemove = [];
    
    // 全セルの位置をリストアップ
    const allCells = [];
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        allCells.push({ row, col });
      }
    }
    
    // ランダムに順序を入れ替え
    for (let i = allCells.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allCells[i], allCells[j]] = [allCells[j], allCells[i]];
    }
    
    // 一意性を保ちながら消せるマスを見つける
    for (const cell of allCells) {
      if (cellsToRemove.length >= count) break;
      
      const { row, col } = cell;
      if (boardCopy[row][col] === 0) continue;
      
      // 一時的にマスを消す
      const originalValue = boardCopy[row][col];
      boardCopy[row][col] = 0;
      
      // 一意解チェック
      const result = hasUniqueSolution(boardCopy);
      
      if (result.isUnique) {
        // このマスは安全に消せる
        cellsToRemove.push({ row, col, value: originalValue });
      } else {
        // このマスは消せないので元に戻す
        boardCopy[row][col] = originalValue;
      }
    }
    
    setInitialRemovalInProgress(true);
    
    // アニメーション付きで順番に消していく
    cellsToRemove.forEach((cell, index) => {
      setTimeout(() => {
        setBoard(prevBoard => {
          const newBoard = prevBoard.map(row => [...row]);
          newBoard[cell.row][cell.col] = 0;
          return newBoard;
        });
        setRemovedCells(prev => prev + 1);
        
        // 最後のセルが消えたらアニメーション完了
        if (index === cellsToRemove.length - 1) {
          setInitialRemovalInProgress(false);
        }
      }, index * 100); // 100ミリ秒ごとに1つのマスを消去
    });
  };

  // メインメニューから通常ゲームを開始
  const handleStartGame = () => {
    setShowMainMenu(false);
    setGameMode('normal');
    startNewGame();
  };
  
  // メインメニューから数独危機一髪モードを開始
  const handleStartBombGame = () => {
    setShowMainMenu(false);
    setGameMode('bomb');
    startBombGame();
  };
  
  // メインメニューからおまけ：数独モードを開始
  const handleStartNormalSudoku = () => {
    setShowMainMenu(false);
    setGameMode('sudoku');
    startNormalSudokuGame();
  };

  // ゲームからメインメニューに戻る
  const handleBackToMenu = () => {
    setShowMainMenu(true);
    setBombGameResult(null);
    setSudokuCheckResult(null);
  };

  // 通常モードの新しいゲームを開始
  const startNewGame = () => {
    setIsLoading(true);
    setInitialRemovalInProgress(false);
    
    // 非同期で数独を生成（UI をブロックしないため）
    setTimeout(() => {
      const newBoard = generateCompletedSudoku();
      const fullBoard = newBoard.map(row => [...row]);
      setBoard(fullBoard.map(row => [...row]));
      setOriginalBoard(fullBoard.map(row => [...row]));
      setCurrentPlayer(1);
      setGameOver(false);
      setWinner(null);
      setRemovedCells(0);
      setLastRemoved(null);
      setMultipleAnswers([]);
      setOptimalSolution(null);
      setShowOptimalSolution(false);
      setIsLoading(false);
      
      // ゲーム開始時に35マスを自動的に消去する（アニメーション付き）
      setTimeout(() => {
        removeInitialCells(fullBoard, 35);
      }, 500);
    }, 100);
  };
  
  // 数独危機一髪モードの新しいゲームを開始 - 段階的な処理でブラウザをフリーズさせない
  const startBombGame = () => {
    setIsLoading(true);
    setBombGameResult(null);
    
    // ステップ1: 完成した数独盤面を生成
    setTimeout(() => {
      const newBoard = generateCompletedSudoku();
      const fullBoard = newBoard.map(row => [...row]);
      
      // 一時的な盤面を表示して、次のステップへ
      setBoard(fullBoard.map(row => [...row]));
      setOriginalBoard(fullBoard);
      
      // ステップ2: 盤面から初期消去（より簡易版で高速に）
      setTimeout(() => {
        const initialBoard = fullBoard.map(row => [...row]);
        const allCells = [];
        
        // 全セルの位置をリストアップ
        for (let row = 0; row < 9; row++) {
          for (let col = 0; col < 9; col++) {
            allCells.push({ row, col });
          }
        }
        
        // ランダムに20マス程度を消す（簡易的な初期消去）
        shuffleArray(allCells);
        const cellsToRemove = allCells.slice(0, 20);
        
        for (const cell of cellsToRemove) {
          const { row, col } = cell;
          const temp = initialBoard[row][col];
          initialBoard[row][col] = 0;
          
          // 一意解が保たれなければ元に戻す
          if (!hasUniqueSolution(initialBoard).isUnique) {
            initialBoard[row][col] = temp;
          }
        }
        
        // 初期消去した盤面を表示して、次のステップへ
        setBoard(initialBoard);
        
        // ステップ3: あと1マスだけ抜ける状態への調整
        setTimeout(() => {
          try {
            // 最適解を探索して安全に消せるマスを1つ復元した状態を作る
            const { finalBoard, removedCells } = findOptimalSolution(initialBoard);
            
            if (removedCells.length > 0) {
              // 安全に消せるマスを1つランダムに選んで復元
              const randomIndex = Math.floor(Math.random() * removedCells.length);
              const cellToKeep = removedCells[randomIndex];
              
              // 消せるマスを1つ戻す（元の値に復元）
              const finalBoardWithOneCell = finalBoard.map(row => [...row]);
              finalBoardWithOneCell[cellToKeep.row][cellToKeep.col] = fullBoard[cellToKeep.row][cellToKeep.col];
              
              // 消せるマスを計算
              const safeRemovableCells = findAllRemovableCells(finalBoardWithOneCell, fullBoard);
              
              // 最終的な盤面とデータをセット
              setBoard(finalBoardWithOneCell);
              setRemovableCells(safeRemovableCells);
              setIsLoading(false);
            } else {
              // バックアッププラン：初期盤面から数マスだけ消去
              const simpleBoardCopy = fullBoard.map(row => [...row]);
              const simpleCellsToRemove = allCells.slice(0, 10);
              
              for (const cell of simpleCellsToRemove) {
                const { row, col } = cell;
                const temp = simpleBoardCopy[row][col];
                simpleBoardCopy[row][col] = 0;
                
                if (!hasUniqueSolution(simpleBoardCopy).isUnique) {
                  simpleBoardCopy[row][col] = temp;
                }
              }
              
              // 消せるマスを計算
              const finalRemovableCells = findAllRemovableCells(simpleBoardCopy, fullBoard);
              
              // 最終的な盤面とデータをセット
              setBoard(simpleBoardCopy);
              setRemovableCells(finalRemovableCells);
              setIsLoading(false);
            }
          } catch (error) {
            // エラー発生時のフォールバック：元の盤面から少しだけ消して表示
            console.error('Error generating bomb game board:', error);
            
            const fallbackBoard = fullBoard.map(row => [...row]);
            const fallbackCellsToRemove = allCells.slice(0, 5);
            
            for (const cell of fallbackCellsToRemove) {
              const { row, col } = cell;
              fallbackBoard[row][col] = 0;
            }
            
            // 消せるマスを計算
            const fallbackRemovableCells = findAllRemovableCells(fallbackBoard, fullBoard);
            
            // 最終的な盤面とデータをセット
            setBoard(fallbackBoard);
            setRemovableCells(fallbackRemovableCells.length > 0 ? fallbackRemovableCells : [{ row: 0, col: 0, value: fallbackBoard[0][0] }]);
            setIsLoading(false);
          }
        }, 300); // ステップ3のタイムアウト
      }, 300); // ステップ2のタイムアウト
    }, 100); // ステップ1のタイムアウト
  };
  
  // おまけ：数独モードの新しいゲームを開始
  const startNormalSudokuGame = () => {
    setIsLoading(true);
    setSudokuCheckResult(null);
    
    // ステップ1: 完成した数独盤面を生成
    setTimeout(() => {
      const completedBoard = generateCompletedSudoku();
      
      // 完成した盤面を保存
      setOriginalBoard(Array(9).fill().map(() => Array(9).fill(0)));
      setBoard(completedBoard.map(row => [...row]));
      
      // ステップ2: 最大限にマスを抜く（ただし一意解を保持）
      setTimeout(() => {
        try {
          // 最適解（最大限マスを抜いた状態）を計算
          const { finalBoard, removedCells } = findOptimalSolution(completedBoard);
          
          // 原盤面（ヒント）とプレイヤー盤面を設定
          setOriginalBoard(finalBoard.map(row => [...row]));
          setBoard(finalBoard.map(row => [...row]));
          setIsLoading(false);
        } catch (error) {
          console.error('Error generating sudoku puzzle:', error);
          
          // エラー時は簡易版で対応
          const simplifiedBoard = completedBoard.map(row => [...row]);
          const allCells = [];
          
          for (let row = 0; row < 9; row++) {
            for (let col = 0; col < 9; col++) {
              allCells.push({ row, col });
            }
          }
          
          // ランダムに45マス程度を消す
          shuffleArray(allCells);
          const cellsToRemove = allCells.slice(0, 45);
          
          for (const cell of cellsToRemove) {
            const { row, col } = cell;
            const temp = simplifiedBoard[row][col];
            simplifiedBoard[row][col] = 0;
            
            // 一意解が保たれなければ元に戻す
            if (!hasUniqueSolution(simplifiedBoard).isUnique) {
              simplifiedBoard[row][col] = temp;
            }
          }
          
          // 盤面を設定
          setOriginalBoard(simplifiedBoard.map(row => [...row]));
          setBoard(simplifiedBoard.map(row => [...row]));
          setIsLoading(false);
        }
      }, 300);
    }, 100);
  };

  // 配列をシャッフルする関数（Fisher-Yates アルゴリズム） - App.js内でも使えるように定義
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  // 通常モードでセルをクリックしたときのハンドラ
  const handleCellClick = (row, col) => {
    if (gameOver || board[row][col] === 0 || isLoading || initialRemovalInProgress) return;
    
    // セルの値を保存（解説用）
    const removedValue = board[row][col];
    
    // セルの値を0に設定（数字を消す）
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = 0;
    
    // 一意解チェック（非同期処理）
    setIsLoading(true);
    
    setTimeout(() => {
      const result = hasUniqueSolution(newBoard);
      
      if (!result.isUnique) {
        setLastRemoved({ row, col, value: removedValue });
        setMultipleAnswers(result.solutions);
        setGameOver(true);
        setWinner(currentPlayer === 1 ? 2 : 1);
        
        // 一つ前のボードを記録し、それに基づいて最適解を計算
        const previousBoard = board.map(r => [...r]);
        const optimal = findOptimalSolution(previousBoard);
        setOptimalSolution(optimal);
      } else {
        setBoard(newBoard);
        setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
        setRemovedCells(prev => prev + 1);
      }
      
      setIsLoading(false);
    }, 100);
  };
  
  // 数独危機一髪モードでセルをクリックしたときのハンドラ
  const handleBombCellClick = (row, col) => {
    if (isLoading || board[row][col] === 0) return;
    
    // 選択されたセルの情報
    const selectedCell = { row, col, value: board[row][col] };
    
    // 正解かどうかチェック
    const isCorrectMove = removableCells.some(
      cell => cell.row === row && cell.col === col
    );
    
    // 結果をセット
    if (isCorrectMove) {
      // 正解の場合
      setBombGameResult({
        isSuccess: true,
        selectedCell
      });
    } else {
      // 不正解の場合
      const explanation = explainWhyNotRemovable(board, row, col);
      setBombGameResult({
        isSuccess: false,
        selectedCell,
        explanation
      });
    }
  };
  
  // おまけ：数独モードでセルに数字を入力したときのハンドラ
  const handleSudokuCellInput = (row, col, number, fullBoard = null) => {
    if (isLoading) return;
    
    // 完全な盤面が指定された場合（保存データ読み込み用）
    if (fullBoard) {
      setBoard(fullBoard);
      return;
    }
    
    // セルを1つおきに盤面を更新
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = number;
    setBoard(newBoard);
  };
  
  // おまけ：数独モードで判定ボタンをクリックしたときのハンドラ
  const handleSudokuCheck = () => {
    // 数独ルールに沿っているかチェック
    const isValid = checkSudokuValidity(board);
    setSudokuCheckResult(isValid);
  };
  
  // 数独の正当性をチェックする関数は sudokuUtils.js に移動
  
  // チュートリアルの表示切り替え
  const toggleTutorial = () => {
    setShowTutorial(!showTutorial);
  };

  // メインメニューの表示
  if (showMainMenu) {
    return <MainMenu 
      onStartGame={handleStartGame} 
      onStartBombGame={handleStartBombGame} 
      onStartNormalSudoku={handleStartNormalSudoku}
    />;
  }

  // ゲーム画面の表示
  return (
    <div className="app">
      <header className="header">
        <h1>{gameMode === 'bomb' ? '数独危機一髪' : gameMode === 'sudoku' ? 'おまけ：数独' : '数独マスター'}</h1>
        <div className="header-buttons">
          <button onClick={toggleTutorial}>
            {showTutorial ? "チュートリアルを閉じる" : "遊び方"}
          </button>
          <button 
            onClick={gameMode === 'bomb' ? startBombGame : gameMode === 'sudoku' ? startNormalSudokuGame : startNewGame} 
            disabled={isLoading || initialRemovalInProgress}
          >
            新しいゲーム
          </button>
          <button onClick={handleBackToMenu} className="back-button">
            メニューに戻る
          </button>
        </div>
      </header>

      {showTutorial && (
        gameMode === 'bomb' 
          ? <BombGameTutorial onClose={toggleTutorial} />
          : <Tutorial onClose={toggleTutorial} />
      )}

      <main className="main-content">
        {isLoading ? (
          <div className="loading">
            <p>読み込み中...</p>
          </div>
        ) : gameMode === 'bomb' ? (
          // 数独危機一髪モードの表示
          <>
            {!bombGameResult ? (
              <SudokuBombGame 
                board={board}
                onCellClick={handleBombCellClick}
                isLoading={isLoading}
                removableCells={removableCells}
              />
            ) : (
              <BombGameResult 
                isSuccess={bombGameResult.isSuccess}
                selectedCell={bombGameResult.selectedCell}
                explanation={bombGameResult.explanation}
                correctCells={removableCells}
                onPlayAgain={startBombGame}
                onBackToMenu={handleBackToMenu}
                board={board} // 追加：盤面データを渡す
              />
            )}
          </>
        ) : gameMode === 'sudoku' ? (
          // おまけ：数独モードの表示
          <>
            {!sudokuCheckResult ? (
              <NormalSudokuGame
                board={board}
                originalBoard={originalBoard}
                onCellInput={handleSudokuCellInput}
                isLoading={isLoading}
                onCheck={handleSudokuCheck}
              />
            ) : (
              <SudokuResult
                isCorrect={sudokuCheckResult}
                onPlayAgain={startNormalSudokuGame}
                onBackToMenu={handleBackToMenu}
              />
            )}
          </>
        ) : (
          // 通常モードの表示
          <>
            <GameControls 
              currentPlayer={currentPlayer} 
              removedCells={removedCells}
              initialRemovalInProgress={initialRemovalInProgress}
            />
            
            <SudokuBoard 
              board={board} 
              onCellClick={handleCellClick} 
              isLoading={isLoading}
              initialRemovalInProgress={initialRemovalInProgress}
            />

            {gameOver && lastRemoved && (
              <GameOver 
                winner={winner} 
                lastRemoved={lastRemoved} 
                multipleAnswers={multipleAnswers}
                optimalSolution={optimalSolution}
                showOptimalSolution={showOptimalSolution}
                setShowOptimalSolution={setShowOptimalSolution}
                onBackToMenu={handleBackToMenu}
                onPlayAgain={startNewGame}
              />
            )}
          </>
        )}
      </main>

      <footer className="footer">
        <p>© 2025 数独マスター - 一意解の世界を探検するゲーム</p>
      </footer>
    </div>
  );
}

export default App;